import { WorkPage, WorkSection, WorkEmpty } from '../components/work-page.tsx';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { notificationsApi } from '../api/endpoints.ts';
import { Alert, Button, LoadingBlock, Pagination } from '../components/ui.tsx';
import { NOTIFICATION_LIST_KEY, useInvalidateNotifications } from '../components/notification-bell.tsx';
import { useDocumentTitle } from '../hooks/index.ts';
import { formatShortInstant } from '../i18n/datetime.ts';
import type { AppNotification } from '@shared/types/index.ts';

/**
 * The bell's own page: everything the dropdown holds plus the history the
 * dropdown truncates, filterable to unread, paged.
 *
 * The list is per person by construction — the server only ever answers with
 * the session's own rows, already tailored to role and working location when
 * they were written — so this page carries no permission gate of its own.
 */
export default function NotificationsPage() {
  const { t } = useTranslation();
  useDocumentTitle(t('notifications.documentTitle'));
  const navigate = useNavigate();
  const invalidate = useInvalidateNotifications();

  const [filter, setFilter] = useState<'all' | 'unread'>('all');
  const [page, setPage] = useState(1);

  const query = useQuery({
    queryKey: [...NOTIFICATION_LIST_KEY, { filter, page }],
    queryFn: () => notificationsApi.list({ filter, page, pageSize: 25 }),
  });

  const markRead = useMutation({
    mutationFn: (ids?: string[]) => notificationsApi.markRead(ids),
    onSettled: invalidate,
  });

  function follow(notification: AppNotification) {
    if (!notification.readAt) markRead.mutate([notification.id]);
    if (notification.linkPath) navigate(notification.linkPath);
  }

  const unread = query.data?.unread ?? 0;

  return (
    <WorkPage title={t('notifications.title')} description={t('notifications.description')}>
      <WorkSection title={t('notifications.filterLabel')}>
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="flex flex-wrap items-center gap-1" role="group" aria-label={t('notifications.filterLabel')}>
            {(['all', 'unread'] as const).map((value) => (
              <button
                key={value}
                type="button"
                aria-pressed={filter === value}
                onClick={() => {
                  setFilter(value);
                  setPage(1);
                }}
                className={`rounded-md px-3 py-1.5 text-sm font-medium ${
                  filter === value
                    ? 'bg-[color:var(--color-brand-soft)] text-[color:var(--color-brand-strong)]'
                    : 'text-[color:var(--color-muted)] hover:bg-[color:var(--color-surface)]'
                }`}
              >
                {value === 'all' ? t('notifications.filterAll') : t('notifications.filterUnread', { count: unread })}
              </button>
            ))}
          </div>
          <Button
            variant="secondary"
            disabled={unread === 0}
            busy={markRead.isPending}
            onClick={() => markRead.mutate(undefined)}
          >
            {t('notifications.markAllRead')}
          </Button>
        </div>

        {query.isLoading ? <LoadingBlock label={t('notifications.loading')} /> : null}
        {query.isError ? (
          <Alert tone="error" title={t('notifications.loadErrorTitle')}>
            {(query.error as Error).message}
          </Alert>
        ) : null}

        {!query.isError && query.data && query.data.items.length === 0 ? (
          <WorkEmpty title={filter === 'unread' ? t('notifications.emptyUnread') : t('notifications.empty')}>
            <p>{t('notifications.emptyBody')}</p>
          </WorkEmpty>
        ) : null}

        {!query.isError && query.data && query.data.items.length > 0 ? (
          <ul className="divide-y divide-[color:var(--color-line)] p-0">
            {query.data.items.map((notification) => (
              <li key={notification.id}>
                <button
                  type="button"
                  onClick={() => follow(notification)}
                  className="grid w-full grid-cols-[auto_minmax(0,1fr)] items-start gap-x-3 gap-y-2 px-2 py-3 text-left hover:bg-[color:var(--color-surface)] sm:grid-cols-[auto_minmax(0,1fr)_auto]"
                >
                  <span
                    aria-hidden="true"
                    className={`mt-1.5 h-2 w-2 shrink-0 rounded-full ${
                      notification.readAt ? 'bg-transparent' : 'bg-[color:var(--color-brand)]'
                    }`}
                  />
                  <span className="min-w-0">
                    <span className={`block text-sm ${notification.readAt ? 'font-medium' : 'font-semibold'}`}>
                      {notification.title}
                      {!notification.readAt ? (
                        <span className="sr-only"> ({t('notifications.unreadMarker')})</span>
                      ) : null}
                    </span>
                    {notification.body ? (
                      <span className="block text-sm text-[color:var(--color-muted)]">{notification.body}</span>
                    ) : null}
                  </span>
                  <span className="col-start-2 min-w-0 text-xs text-[color:var(--color-muted)] sm:col-start-auto">
                    {formatShortInstant(notification.createdAt)}
                  </span>
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        <div className="mt-4">
          <Pagination page={page} totalPages={query.data?.totalPages} onPageChange={setPage} />
        </div>
      </WorkSection>
    </WorkPage>
  );
}
